import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltIcon from "@mui/icons-material/Bolt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckIcon from "@mui/icons-material/Check";
import FilterListIcon from "@mui/icons-material/FilterList";
import HistoryIcon from "@mui/icons-material/History";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SearchIcon from "@mui/icons-material/Search";
import UploadIcon from "@mui/icons-material/Upload";
import api from "../../axiosConfig";
import { parseExcelOrCsv } from "./utils/importParse";
import { emailContactsApi } from "./api/emailContactsApi";
import {
  buildAudienceNarrative,
  buildAudiencePlan,
  buildAudienceStatusBadges,
  getAudiencePreset,
  isImportAudience,
  isManualAudience,
  normalizePositiveInteger,
  toAudienceRow,
} from "./audienceUtils";

export { toAudienceRow };

// ─── Static card definitions ──────────────────────────────────────────────────

const QUICK_SEGMENTS = [
  {
    key: "all_eligible",
    label: "All eligible",
    icon: <PeopleAltIcon fontSize="small" />,
    iconBg: "#F0FDF4",
    iconColor: "#047857",
    description: "Every active contact passing delivery guardrails.",
  },
  {
    key: "never_contacted",
    label: "Never contacted",
    icon: <MailOutlineIcon fontSize="small" />,
    iconBg: "#EFF6FF",
    iconColor: "#1D4ED8",
    description: "Have never received an email broadcast.",
  },
  {
    key: "hot_leads",
    label: "Hot leads",
    icon: <LocalFireDepartmentIcon fontSize="small" />,
    iconBg: "#FEF2F2",
    iconColor: "#B91C1C",
    description: "Tied to hot or urgent inquiries.",
  },
  {
    key: "reengage_30",
    label: "Re-engage 30+ days",
    icon: <HistoryIcon fontSize="small" />,
    iconBg: "#FFFBEB",
    iconColor: "#92400E",
    description: "Not emailed in the last 30 days.",
  },
];

const CUSTOM_SEGMENTS = [
  {
    key: "imported_list",
    label: "Imported list",
    icon: <UploadIcon fontSize="small" />,
    iconBg: "#F4F5F4",
    iconColor: "#444441",
    description: "Upload a CSV or Excel file and send to that list only.",
  },
  {
    key: "custom_audience",
    label: "Custom audience",
    icon: <FilterListIcon fontSize="small" />,
    iconBg: "#F4F5F4",
    iconColor: "#444441",
    description: "Filter and manually review exactly who gets it.",
  },
  {
    key: "my_contacts",
    label: "My contacts",
    icon: <PeopleAltIcon fontSize="small" />,
    iconBg: "#F0FDF4",
    iconColor: "#047857",
    description: "Send to contacts you created in My Contacts.",
  },
];

const PACING_OPTIONS = [
  { label: "All at once", mode: "immediate", days: 0 },
  { label: "Over 7 days",  mode: "spread",    days: 7  },
  { label: "Over 30 days", mode: "spread",    days: 30 },
];

const SOURCE_OPTIONS = [
  { value: "",           label: "All sources"      },
  { value: "hubspot",    label: "HubSpot"          },
  { value: "google",     label: "Google Contacts"  },
  { value: "inquiries",  label: "Inquiries"        },
  { value: "manual",     label: "Manual contacts"  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toBudgetInputValue(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

function parseBudgetInputValue(value, fallback = null) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return fallback;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : Number.NaN;
}

function getSkipReasonColor(reason) {
  const r = String(reason).toLowerCase();
  if (r.includes("emailed") || r.includes("recent") || r.includes("lookback")) return "#D97706";
  if (r.includes("unsubscribed") || r.includes("opt-out") || r.includes("opted")) return "#B91C1C";
  if (r.includes("bounce") || r.includes("invalid")) return "#5F6B66";
  if (r.includes("budget") || r.includes("cap") || r.includes("limit")) return "#1D4ED8";
  return "#9CA3AF";
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function AudienceBuilder({
  // audienceMode / onAudienceModeChange removed — mode toggle dropped in Phase 1
  audienceChoice,
  onAudienceChoiceChange,
  source,
  onSourceChange,
  filters,
  onFiltersChange,
  loading,
  setLoading,
  excludeRecent,
  setExcludeRecent,
  recentDays,
  setRecentDays,
  audienceSummary,
  previewRows,
  onAudiencePrepared,
  onImportRows,
  onUseAudience,
  onFindSuccess,       // kept for interface compatibility; not called from auto-estimate
  emailBudget,
  budgetLoading,
  canManageBudget = false,
  onEmailBudgetUpdated,
  intelligenceAudienceId = null, // kept for interface compatibility; ignored in Phase 1
  // Delivery pacing — owned by the parent so it can be submitted with the campaign.
  pacingMode = "immediate",
  onPacingModeChange = () => {},
  pacingDays = 0,
  onPacingDaysChange = () => {},
}) {
  const [importLabel, setImportLabel] = useState("No file selected");
  const [parsingFile, setParsingFile] = useState(false);
  const [requestError, setRequestError] = useState("");

  // segmentCounts: quick-segment key → pre-fetched eligible count (null = still loading)
  const [segmentCounts, setSegmentCounts] = useState({});

  // Delivery pacing is now controlled by the parent (props pacingMode/pacingDays).

  // Custom audience search (local value; debounced before propagating to filters)
  const [searchQ, setSearchQ] = useState(filters.q || "");
  const searchDebounceRef = useRef(null);

  // "Adjust settings" dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [localExcludeRecent, setLocalExcludeRecent] = useState(excludeRecent);
  const [localRecentDays, setLocalRecentDays] = useState(recentDays);

  // "Adjust shared cap" dialog
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);

  // Budget editor state — UI surface moved to Phase 2, logic retained here
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetError, setBudgetError] = useState("");
  const [budgetForm, setBudgetForm] = useState({
    daily_limit: "",
    monthly_limit: "",
    reserve_buffer: "",
  });

  const estimateCounterRef = useRef(0);

  // ─── Derived values ───────────────────────────────────────────────────────

  const preset = getAudiencePreset(audienceChoice);
  const usingImport = isImportAudience(audienceChoice, source);
  const usingManualSelection = isManualAudience(audienceChoice);
  const audienceBadges = buildAudienceStatusBadges(audienceSummary);
  const sampleSize = normalizePositiveInteger(filters.limit) || 200;
  const preferredLimit = normalizePositiveInteger(filters.preferred_limit);

  const budgetRemaining =
    emailBudget?.remaining === null || emailBudget?.remaining === undefined
      ? null
      : Number(emailBudget.remaining);
  const budgetReserve = Number(emailBudget?.reserve_buffer || 0);
  const budgetCostPerRecipient = Math.max(0.0001, Number(emailBudget?.cost_per_recipient || 1));
  const budgetSendable = Number.isFinite(budgetRemaining)
    ? Math.max(0, Math.floor((budgetRemaining - budgetReserve) / budgetCostPerRecipient))
    : null;

  const audienceNarrative = useMemo(
    () => buildAudienceNarrative({ audienceKey: audienceChoice, source, filters, summary: audienceSummary }),
    [audienceChoice, source, filters, audienceSummary]
  );

  const canUseAudience = useMemo(() => {
    if (!audienceSummary) return false;
    if (usingImport || usingManualSelection) {
      return Number(audienceSummary.totalFound || audienceSummary.eligibleFound || 0) > 0;
    }
    return Number(audienceSummary.eligibleFound || 0) > 0;
  }, [audienceSummary, usingImport, usingManualSelection]);

  // Count shown in CTA and pacing preview: prefer live summary, fall back to pre-fetched pill
  const ctaCount = audienceSummary?.eligibleFound ?? segmentCounts[audienceChoice] ?? null;

  const skipBreakdownEntries = useMemo(() => {
    const bd = audienceSummary?.suppressedBreakdown || {};
    return Object.entries(bd).filter(([, count]) => Number(count) > 0);
  }, [audienceSummary]);

  // ─── Pacing preview text ─────────────────────────────────────────────────

  const pacingPreview = useMemo(() => {
    if (pacingMode === "immediate") {
      return ctaCount !== null
        ? { icon: "bolt", text: <>Sends all <strong>{Number(ctaCount).toLocaleString()}</strong> contacts immediately.</> }
        : { icon: "bolt", text: "Sends contacts immediately once an audience is set." };
    }
    if (ctaCount !== null && ctaCount > 0) {
      const perDay = Math.ceil(Number(ctaCount) / pacingDays);
      return {
        icon: "calendar",
        text: <>About <strong>{perDay.toLocaleString()}</strong> contacts/day for <strong>{pacingDays} days</strong>.</>,
      };
    }
    return { icon: "calendar", text: `Will spread across ${pacingDays} days once an audience is set.` };
  }, [pacingMode, pacingDays, ctaCount]);

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    setBudgetForm({
      daily_limit: toBudgetInputValue(emailBudget?.settings?.daily_limit ?? emailBudget?.daily_limit),
      monthly_limit: toBudgetInputValue(emailBudget?.settings?.monthly_limit ?? emailBudget?.monthly_limit),
      reserve_buffer: toBudgetInputValue(emailBudget?.settings?.reserve_buffer ?? emailBudget?.reserve_buffer ?? 0),
    });
  }, [emailBudget]);

  // Sync local settings dialog state when it opens
  useEffect(() => {
    if (settingsOpen) {
      setLocalExcludeRecent(excludeRecent);
      setLocalRecentDays(recentDays);
    }
  }, [settingsOpen, excludeRecent, recentDays]);

  // Pre-fetch counts for all quick segments on mount (lightweight, count-only calls)
  useEffect(() => {
    const segments = [
      { key: "all_eligible",    segment: "",                excludeRecent: true,  days: 7  },
      { key: "never_contacted", segment: "never_contacted", excludeRecent: false, days: 7  },
      { key: "hot_leads",       segment: "hot_leads",       excludeRecent: false, days: 7  },
      { key: "reengage_30",     segment: "",                excludeRecent: true,  days: 30 },
    ];

    Promise.all(
      segments.map(async ({ key, segment, excludeRecent: excl, days }) => {
        try {
          const res = await emailContactsApi.estimate({ limit: 1, segment, excludeRecent: excl, days, active: true });
          return [key, Number(res.eligible_found ?? 0)];
        } catch {
          return [key, null];
        }
      })
    ).then((results) => {
      setSegmentCounts((prev) => ({ ...prev, ...Object.fromEntries(results) }));
    });
  }, []); // runs once on mount

  // Auto-run a full estimate whenever a quick segment or guardrail changes.
  // Phase 2 will extend deps to include filter changes (q, source, preferred_limit).
  useEffect(() => {
    if (usingImport) {
      setLoading(false);
      return;
    }

    const ticket = ++estimateCounterRef.current;
    setLoading(true);
    setRequestError("");

    (async () => {
      try {
        const response = await emailContactsApi.estimate({
          q: filters.q || "",
          source: filters.source || null,
          active: true,
          limit: sampleSize,
          preferredLimit,
          excludeRecent,
          days: Number(recentDays || preset.defaultDays || 7),
          segment: preset.segment || filters.segment || "",
          audienceKind: preset.source === "my_contacts" ? "my_contacts" : null,
          includeMyContacts: audienceChoice === "all_eligible" ? true : null,
        });

        if (ticket !== estimateCounterRef.current) return;

        const eligibleCount = Number(response.eligible_found ?? 0);
        setSegmentCounts((prev) => ({ ...prev, [audienceChoice]: eligibleCount }));

        const summary = {
          totalFound: Number(response.total_found || 0),
          eligibleFound: eligibleCount,
          suppressedFound: Number(response.suppressed_found ?? 0),
          estimatedCost: Number(response.estimated_cost ?? 0),
          remainingAfterEstimate:
            response.remaining_after_estimate === null || response.remaining_after_estimate === undefined
              ? null
              : Number(response.remaining_after_estimate),
          costPerRecipient: Number(response.cost_per_recipient ?? 0),
          budgetSendableToday:
            response.budget_sendable_today === null || response.budget_sendable_today === undefined
              ? null
              : Number(response.budget_sendable_today),
          budget: response.budget || null,
          suppressedEmails: Array.isArray(response.suppressedEmails) ? response.suppressedEmails : [],
          suppressedBreakdown:
            response.suppressed_breakdown && typeof response.suppressed_breakdown === "object"
              ? response.suppressed_breakdown
              : {},
          sampleSize: Number(response.sample_size || sampleSize),
          sampleReturned: Number(response.sample_returned || 0),
          preferredLimit: response.preferred_limit ? Number(response.preferred_limit) : null,
        };

        onAudiencePrepared?.({
          summary,
          previewRows: (response.contacts || []).map(toAudienceRow),
          plan: buildAudiencePlan({
            audienceKey: audienceChoice,
            source,
            filters: { ...filters, segment: preset.segment || filters.segment || "" },
            excludeRecent,
            recentDays,
            summary,
          }),
        });
        setRequestError("");
      } catch (error) {
        if (ticket !== estimateCounterRef.current) return;
        setRequestError(
          error?.response?.data?.message ||
            error?.message ||
            "We could not load this audience right now. Please try again."
        );
        onAudiencePrepared?.(null);
      } finally {
        if (ticket === estimateCounterRef.current) {
          setLoading(false);
        }
      }
    })();
  }, [audienceChoice, excludeRecent, recentDays, filters.q, filters.source, filters.preferred_limit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const clearPreparedAudience = () => {
    setRequestError("");
    onAudiencePrepared?.(null);
  };

  const handleSearchChange = (val) => {
    setSearchQ(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      onFiltersChange({ ...filters, q: val });
    }, 400);
  };

  const handlePresetSelect = (nextKey) => {
    const nextPreset = getAudiencePreset(nextKey);
    clearPreparedAudience();
    onAudienceChoiceChange(nextKey);
    setSearchQ("");
    onSourceChange(nextPreset.source);
    onFiltersChange({
      ...filters,
      q: nextKey === "custom_audience" ? filters.q : "",
      source: "",
      segment: nextPreset.segment || "",
      limit: nextKey === "custom_audience" ? 200 : filters.limit || 200,
      preferred_limit: filters.preferred_limit || "",
    });
    setExcludeRecent(nextPreset.defaultExcludeRecent);
    setRecentDays(nextPreset.defaultDays);
    setImportLabel("No file selected");
  };

  const handlePickFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setParsingFile(true);
    setImportLabel(`${file.name} — parsing...`);

    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      const rows = await parseExcelOrCsv(file);
      if (!rows.length) {
        setImportLabel(`${file.name} - no valid emails found`);
        clearPreparedAudience();
        onImportRows?.([]);
        return;
      }

      handlePresetSelect("imported_list");
      setImportLabel(`${file.name} - ${rows.length.toLocaleString()} ready`);
      onImportRows?.(rows);

      const summary = {
        totalFound: rows.length,
        eligibleFound: preferredLimit ? Math.min(rows.length, preferredLimit) : rows.length,
        suppressedFound: preferredLimit ? Math.max(0, rows.length - preferredLimit) : 0,
        estimatedCost: 0,
        remainingAfterEstimate: null,
        costPerRecipient: 0,
        suppressedEmails: [],
        suppressedBreakdown:
          preferredLimit && rows.length > preferredLimit
            ? { "Preferred campaign limit reached": rows.length - preferredLimit }
            : {},
        preferredLimit,
      };

      onAudiencePrepared?.({
        summary,
        previewRows: rows.slice(0, sampleSize),
        plan: buildAudiencePlan({
          audienceKey: "imported_list",
          source: "import",
          filters,
          excludeRecent,
          recentDays,
          summary,
        }),
      });
      setRequestError("");
    } catch (error) {
      clearPreparedAudience();
      onImportRows?.([]);
      setImportLabel(`${file.name} — import failed`);
      setRequestError(
        error?.message || "We could not read that file. Please try a CSV or Excel file with an email column."
      );
    } finally {
      setLoading(false);
      setParsingFile(false);
    }
  };

  const handleApplySettings = () => {
    setExcludeRecent(localExcludeRecent);
    setRecentDays(localRecentDays);
    setSettingsOpen(false);
  };

  // Budget save — UI surface is Phase 2; logic kept here for continuity
  const handleSaveSharedCap = async () => {
    const dailyLimit = parseBudgetInputValue(budgetForm.daily_limit);
    const monthlyLimit = parseBudgetInputValue(budgetForm.monthly_limit);
    const reserveBuffer = parseBudgetInputValue(budgetForm.reserve_buffer, 0);

    if (Number.isNaN(dailyLimit) || Number.isNaN(monthlyLimit) || Number.isNaN(reserveBuffer)) {
      setBudgetError("Shared cap values must be valid numbers.");
      return;
    }

    setBudgetSaving(true);
    setBudgetError("");

    try {
      const response = await api.put("/analytics/marketing/broadcast-budget/email", {
        daily_limit: dailyLimit,
        monthly_limit: monthlyLimit,
        reserve_buffer: reserveBuffer ?? 0,
      });

      const channel = response?.data?.data?.channels?.email || null;
      onEmailBudgetUpdated?.(channel);
      clearPreparedAudience();
    } catch (error) {
      setBudgetError(
        error?.response?.data?.message || error?.message || "We could not save the shared email cap right now."
      );
    } finally {
      setBudgetSaving(false);
    }
  };

  // ─── CTA label ────────────────────────────────────────────────────────────

  const ctaLabel = usingManualSelection
    ? "Review & Pick Recipients"
    : ctaCount !== null
      ? `Continue with ${Number(ctaCount).toLocaleString()} contacts`
      : "Continue";

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {loading ? <LinearProgress sx={{ mb: 2, borderRadius: 1 }} /> : null}

      {/* ── Page header ── */}
      <Stack spacing={0.75} sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: "-0.015em" }}>
          Who should receive this?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pick an audience — counts update live as you change selection.
        </Typography>
      </Stack>

      {/* ── Group 1: Quick segments (4-col → 2-col → 1-col) ── */}
      <Box sx={{ mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 1.5, px: 0.25 }}
        >
          <Typography
            variant="overline"
            fontWeight={700}
            sx={{ fontSize: "0.7rem", letterSpacing: "0.08em", color: "text.secondary" }}
          >
            Quick segments
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Pre-built lists · updated hourly
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          }}
        >
          {QUICK_SEGMENTS.map((cardDef) => (
            <AudienceCard
              key={cardDef.key}
              cardDef={cardDef}
              isSelected={audienceChoice === cardDef.key}
              count={segmentCounts[cardDef.key]}
              onClick={() => handlePresetSelect(cardDef.key)}
            />
          ))}
        </Box>
      </Box>

      {/* ── Group 2: Bring your own (2-col → 1-col) ── */}
      <Box sx={{ mb: 3 }}>
        <Stack sx={{ mb: 1.5, px: 0.25 }}>
          <Typography
            variant="overline"
            fontWeight={700}
            sx={{ fontSize: "0.7rem", letterSpacing: "0.08em", color: "text.secondary" }}
          >
            Bring your own
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          }}
        >
          {CUSTOM_SEGMENTS.map((cardDef) => (
            <AudienceCard
              key={cardDef.key}
              cardDef={cardDef}
              isSelected={audienceChoice === cardDef.key}
              count={undefined}
              onClick={() => handlePresetSelect(cardDef.key)}
            />
          ))}
        </Box>
      </Box>

      {/* ── Custom audience filter panel ── */}
      <Collapse in={usingManualSelection} timeout={200} unmountOnExit>
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            Filter contacts
          </Typography>

          {/* Search + Source */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              label="Search by name or email"
              size="small"
              value={searchQ}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 2 }}
            />
            <TextField
              select
              label="Source"
              size="small"
              value={filters.source || ""}
              onChange={(e) => onFiltersChange({ ...filters, source: e.target.value })}
              sx={{ flex: 1, minWidth: 160 }}
            >
              {SOURCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* Skip recently contacted */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={excludeRecent}
                  onChange={(e) => setExcludeRecent(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Skip recently contacted
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Excludes contacts emailed in the last N days. Turn OFF to reach the whole
                    audience on a re-send — this also lifts the topic-cooldown and duplicate
                    checks, so everyone selected is contacted.
                  </Typography>
                </Box>
              }
              sx={{ mr: 0 }}
            />
            {excludeRecent ? (
              <TextField
                label="Days"
                type="number"
                size="small"
                value={recentDays}
                onChange={(e) => setRecentDays(Number(e.target.value || 7))}
                inputProps={{ min: 1, max: 365 }}
                sx={{ width: 110 }}
              />
            ) : null}
          </Stack>

          {/* Per-campaign send limit */}
          <TextField
            label="Per-campaign send limit (optional)"
            type="number"
            size="small"
            value={filters.preferred_limit || ""}
            onChange={(e) =>
              onFiltersChange({ ...filters, preferred_limit: e.target.value || "" })
            }
            inputProps={{ min: 1 }}
            helperText="Cap how many contacts this campaign sends to, regardless of audience size."
            sx={{ mb: emailBudget || budgetLoading ? 2 : 0, maxWidth: 320 }}
            fullWidth
          />

          {/* Shared email budget row */}
          {(emailBudget || budgetLoading) ? (
            <Box sx={{ p: 1.5, borderRadius: 2, border: "1px solid #E5E7EB", bgcolor: "#FAFAFA" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary" }}
                  >
                    Shared email budget
                  </Typography>
                  {budgetLoading ? (
                    <CircularProgress size={12} sx={{ ml: 1, verticalAlign: "middle" }} />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {budgetSendable !== null
                        ? `~${Number(budgetSendable).toLocaleString()} contacts remaining this period`
                        : "No budget cap set"}
                    </Typography>
                  )}
                </Box>
                {canManageBudget && !budgetLoading ? (
                  <Button
                    size="small"
                    onClick={() => setBudgetDialogOpen(true)}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#047857", flexShrink: 0 }}
                  >
                    Adjust shared cap
                  </Button>
                ) : null}
              </Stack>
            </Box>
          ) : null}
        </Paper>
      </Collapse>

      {/* ── File upload (only when imported_list is selected) ── */}
      {usingImport ? (
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              Upload your recipient file
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV or Excel file with at least an email column. The sender will only see a
              small sample during review, not a huge spreadsheet.
            </Typography>
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
              <Button
                component="label"
                variant="contained"
                startIcon={<UploadIcon />}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Import File
                <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={handlePickFile} />
              </Button>
              <Chip label={importLabel} variant="outlined" />
            </Stack>
            {parsingFile ? <LinearProgress sx={{ mt: 1, borderRadius: 1 }} /> : null}
          </Stack>
        </Paper>
      ) : null}

      {/* ── Delivery pacing card ── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={0.5}
          sx={{ mb: 1.5 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Delivery pacing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Spread sends across time, or fire everything at once.
          </Typography>
        </Stack>

        {/* Segmented control */}
        <Box
          role="group"
          aria-label="Delivery pacing"
          sx={{
            display: "flex",
            bgcolor: "#F3F4F6",
            borderRadius: 2,
            p: "3px",
            gap: "2px",
          }}
        >
          {PACING_OPTIONS.map(({ label, mode, days }) => {
            const active = pacingMode === mode && pacingDays === days;
            return (
              <Box
                key={days}
                component="button"
                onClick={() => { onPacingModeChange(mode); onPacingDaysChange(days); }}
                aria-pressed={active}
                sx={{
                  flex: 1,
                  py: 1,
                  px: { xs: 0.5, sm: 1.5 },
                  border: "none",
                  borderRadius: 1.5,
                  fontFamily: "inherit",
                  fontSize: "13px",
                  fontWeight: active ? 600 : 400,
                  color: active ? "text.primary" : "text.secondary",
                  bgcolor: active ? "white" : "transparent",
                  boxShadow: active ? "0 1px 2px rgba(0,0,0,0.07)" : "none",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": { color: "text.primary" },
                  "&:focus-visible": { outline: "2px solid #10B981", outlineOffset: 1 },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>

        {/* Live calculation pill */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mt: 1.5,
            px: 1.5,
            py: 1,
            bgcolor: "rgba(16,185,129,0.08)",
            borderRadius: 2,
          }}
        >
          {pacingPreview.icon === "bolt" ? (
            <BoltIcon sx={{ fontSize: 16, color: "#10B981", flexShrink: 0 }} />
          ) : (
            <CalendarMonthIcon sx={{ fontSize: 16, color: "#10B981", flexShrink: 0 }} />
          )}
          <Typography variant="body2" color="text.secondary">
            {pacingPreview.text}
          </Typography>
        </Stack>
      </Paper>

      {/* ── Audience summary (green card) ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid rgba(16,185,129,0.2)",
          bgcolor: "rgba(16,185,129,0.06)",
        }}
      >
        {/* Error alert */}
        {requestError ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {requestError}
          </Alert>
        ) : null}

        {/* Title + metadata + stats */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "flex-start" }}
          spacing={2}
        >
          {/* Left: title + metadata line */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "#047857", mb: 0.5 }}
            >
              Audience summary
            </Typography>

            {audienceSummary ? (
              <Typography variant="body2" sx={{ color: "#1F6354", lineHeight: 1.6 }}>
                Outcome: <strong style={{ color: "#04342C" }}>{preset.shortLabel}</strong>
                {excludeRecent ? (
                  <>
                    {" · "}Skipping contacts emailed in last{" "}
                    <strong style={{ color: "#04342C" }}>{recentDays} days</strong>
                  </>
                ) : null}
                {" · "}
                <Box
                  component="button"
                  onClick={() => setSettingsOpen(true)}
                  sx={{
                    background: "none",
                    border: "none",
                    p: 0,
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    color: "#085041",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": { textDecoration: "underline" },
                    "&:focus-visible": { outline: "2px solid #10B981", borderRadius: 1, outlineOffset: 1 },
                  }}
                >
                  Adjust settings →
                </Box>
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: "#1F6354" }}>
                {loading ? "Calculating audience…" : "Select an audience above to see the summary."}
              </Typography>
            )}
          </Box>

          {/* Right: stats */}
          {audienceSummary ? (
            <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ flexShrink: 0 }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  sx={{
                    fontSize: 26,
                    fontWeight: 600,
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                    color: "#04342C",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {Number(audienceBadges.sendable || 0).toLocaleString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#1F6354",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                    fontSize: "10.5px",
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  Will send
                </Typography>
              </Box>

              <Box sx={{ textAlign: "right" }}>
                <Typography
                  sx={{
                    fontSize: 26,
                    fontWeight: 600,
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                    color: "text.secondary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {Number(audienceBadges.skipped || 0).toLocaleString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                    fontSize: "10.5px",
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  Will skip
                </Typography>
              </Box>
            </Stack>
          ) : null}
        </Stack>

        {/* Budget pacing note — over-budget recipients are delivered over time, not dropped */}
        {audienceSummary &&
          (() => {
            const willSend = Number(audienceSummary.eligibleFound || 0);
            const perDay = Number(audienceSummary.budgetSendableToday ?? Number.NaN);
            if (!Number.isFinite(perDay) || perDay <= 0 || willSend <= perDay) {
              return null;
            }
            const days = Math.ceil(willSend / perDay);
            return (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                All <strong>{willSend.toLocaleString()}</strong> contacts will be delivered — about{" "}
                <strong>{perDay.toLocaleString()}/day</strong> over ~
                <strong>
                  {days} day{days === 1 ? "" : "s"}
                </strong>{" "}
                to stay within the shared daily email budget. Nothing is dropped.
              </Alert>
            );
          })()}

        {/* Skip breakdown — only when skip count > 0 */}
        {skipBreakdownEntries.length > 0 ? (
          <Stack
            direction="row"
            spacing={2.5}
            flexWrap="wrap"
            alignItems="center"
            useFlexGap
            sx={{
              mt: 2,
              pt: 2,
              borderTop: "1px solid rgba(16,185,129,0.2)",
              gap: { xs: 1.5, sm: 2.5 },
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: "#1F6354", flexShrink: 0 }}
            >
              Why skipped:
            </Typography>
            {skipBreakdownEntries.map(([reason, count]) => (
              <Stack key={reason} direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: getSkipReasonColor(reason),
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" sx={{ color: "#1F6354", whiteSpace: "nowrap" }}>
                  {reason} ({Number(count).toLocaleString()})
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}


        {/* ── Continue CTA ── */}
        <Stack
          direction="row"
          spacing={1.25}
          justifyContent="flex-end"
          flexWrap="wrap"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          {loading ? (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <CircularProgress size={14} sx={{ color: "#10B981" }} />
              <Typography variant="caption" color="text.secondary">
                Calculating audience…
              </Typography>
            </Stack>
          ) : null}

          <Button
            variant="contained"
            color="success"
            onClick={onUseAudience}
            disabled={!canUseAudience || loading}
            endIcon={<ArrowForwardIcon />}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {ctaLabel}
          </Button>
        </Stack>
      </Paper>

      {/* ── Budget adjustment dialog ── */}
      <Dialog open={budgetDialogOpen} onClose={() => setBudgetDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Adjust shared email cap</DialogTitle>
        <DialogContent>
          {budgetError ? <Alert severity="error" sx={{ mb: 2 }}>{budgetError}</Alert> : null}
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Daily limit (emails/day)"
              type="number"
              size="small"
              value={budgetForm.daily_limit}
              onChange={(e) => setBudgetForm((f) => ({ ...f, daily_limit: e.target.value }))}
              inputProps={{ min: 0 }}
              helperText="0 = no daily cap"
              fullWidth
            />
            <TextField
              label="Monthly limit (emails/month)"
              type="number"
              size="small"
              value={budgetForm.monthly_limit}
              onChange={(e) => setBudgetForm((f) => ({ ...f, monthly_limit: e.target.value }))}
              inputProps={{ min: 0 }}
              helperText="0 = no monthly cap"
              fullWidth
            />
            <TextField
              label="Reserve buffer"
              type="number"
              size="small"
              value={budgetForm.reserve_buffer}
              onChange={(e) => setBudgetForm((f) => ({ ...f, reserve_buffer: e.target.value }))}
              inputProps={{ min: 0 }}
              helperText="Keep this many emails in reserve. Defaults to 0."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBudgetDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSaveSharedCap}
            disabled={budgetSaving}
            startIcon={budgetSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Adjust settings dialog ── */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Audience settings</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localExcludeRecent}
                  onChange={(e) => setLocalExcludeRecent(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Skip recently contacted
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Excludes contacts emailed in the last N days. Turn OFF to reach the whole
                    audience on a re-send — this also lifts the topic-cooldown and duplicate
                    checks, so everyone selected is contacted.
                  </Typography>
                </Box>
              }
            />

            {localExcludeRecent ? (
              <TextField
                label="Lookback days"
                type="number"
                size="small"
                value={localRecentDays}
                onChange={(e) => setLocalRecentDays(Number(e.target.value || 7))}
                inputProps={{ min: 1, max: 365 }}
                helperText="Contacts emailed within this window are excluded."
                fullWidth
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSettingsOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApplySettings}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function AudienceCard({ cardDef, isSelected, count, onClick }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-pressed={isSelected}
      sx={{
        width: "100%",
        textAlign: "left",
        background: isSelected ? "#F0FDF4" : "white",
        border: isSelected ? "2px solid #10B981" : "1px solid #E5E7EB",
        borderRadius: 3,
        // compensate for extra border pixel so content doesn't shift on select
        padding: isSelected ? "13px 15px" : "14px 16px",
        cursor: "pointer",
        position: "relative",
        transition: "border-color 0.15s, transform 0.15s, box-shadow 0.15s",
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        minHeight: 64,
        fontFamily: "inherit",
        "&:hover": isSelected
          ? {}
          : {
              borderColor: "#D1D5DB",
              transform: "translateY(-1px)",
              boxShadow: "0 2px 8px rgba(15,30,25,0.04)",
            },
        "&:focus-visible": {
          outline: "2px solid #10B981",
          outlineOffset: 2,
        },
      }}
    >
      {/* Tinted icon */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: cardDef.iconBg,
          color: cardDef.iconColor,
        }}
      >
        {cardDef.icon}
      </Box>

      {/* Card body */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: isSelected ? "#047857" : "text.primary", letterSpacing: "-0.005em" }}
          >
            {cardDef.label}
          </Typography>

          {/* Count pill — undefined means "no count for this card type" */}
          {count !== undefined ? (
            count !== null ? (
              <Chip
                size="small"
                label={Number(count).toLocaleString()}
                sx={{
                  height: 20,
                  fontSize: "11px",
                  fontWeight: 500,
                  bgcolor: isSelected ? "rgba(159,225,203,0.5)" : "#F3F4F6",
                  color: isSelected ? "#047857" : "#6B7280",
                  border: "none",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            ) : (
              <CircularProgress size={12} sx={{ color: "#9CA3AF" }} />
            )
          ) : null}
        </Stack>

        <Typography
          variant="caption"
          sx={{
            color: isSelected ? "#1F6354" : "text.secondary",
            lineHeight: 1.45,
            display: "block",
          }}
        >
          {cardDef.description}
        </Typography>
      </Box>

      {/* Checkmark badge */}
      {isSelected ? (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            width: 18,
            height: 18,
            borderRadius: "50%",
            bgcolor: "#10B981",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckIcon sx={{ fontSize: 12 }} />
        </Box>
      ) : null}
    </Box>
  );
}

function MetricCard({ label, value, tone }) {
  const palette = {
    success: { border: "#DCFCE7", bg: "#F0FDF4" },
    primary: { border: "#DBEAFE", bg: "#EFF6FF" },
    warning: { border: "#FEF3C7", bg: "#FFFBEB" },
  }[tone];

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: palette.border,
        bgcolor: palette.bg,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>
        {Number(value || 0).toLocaleString()}
      </Typography>
    </Box>
  );
}
